/**
 * Utility functions for formatting data in visitor pages
 */

/**
 * Format price from cents to currency string
 * @param {number} priceCents - Price in cents
 * @param {string} currency - Currency code (default: 'AUD')
 * @returns {string} Formatted price string
 */
export function formatPrice(priceCents, currency = 'AUD') {
  const amount = priceCents / 100;
  
  const currencySymbols = {
    AUD: '$',
    USD: '$',
    EUR: '€',
    GBP: '£',
  };

  const symbol = currencySymbols[currency] || currency;
  return `${symbol}${amount.toFixed(2)}`;
}

/**
 * Format date range for workshops
 * @param {string} startAt - ISO date string
 * @param {string} endAt - ISO date string
 * @returns {string} Formatted date range
 */
export function formatDateRange(startAt, endAt) {
  const start = new Date(startAt);
  const end = new Date(endAt);

  const dateOptions = { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric' 
  };

  const timeOptions = { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  };

  const dateStr = start.toLocaleDateString('en-US', dateOptions);
  const startTime = start.toLocaleTimeString('en-US', timeOptions);
  const endTime = end.toLocaleTimeString('en-US', timeOptions);

  // If same day, show: Jan 15, 2025 • 10:00 AM - 1:00 PM
  if (start.toDateString() === end.toDateString()) {
    return `${dateStr} • ${startTime} - ${endTime}`;
  }

  // If different days, show full range
  const endDateStr = end.toLocaleDateString('en-US', dateOptions);
  return `${dateStr} ${startTime} - ${endDateStr} ${endTime}`;
}

/**
 * Format a single date/time
 * @param {string} dateStr - ISO date string
 * @returns {string} Formatted date
 */
export function formatDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

/**
 * Get duration in hours between two dates
 * @param {string} startAt - ISO date string
 * @param {string} endAt - ISO date string
 * @returns {number} Duration in hours
 */
export function getDuration(startAt, endAt) {
  const start = new Date(startAt);
  const end = new Date(endAt);
  const diffMs = end - start;
  return Math.round(diffMs / (1000 * 60 * 60) * 10) / 10; // Round to 1 decimal
}
